import TurndownService from 'turndown'

interface ClipboardEventLike {
  clipboardData: DataTransfer | null
}

const MARKDOWN_PATTERNS: RegExp[] = [
  // Headers, e.g. "# Title"
  /^#{1,6}\s/m,
  // Fenced code blocks
  /```/,
  // Inline code or backticks
  /`[^`]+`/,
  // Markdown links [text](url)
  /\[.+?\]\(.+?\)/,
  // Unordered lists: "- item", "* item", "+ item"
  /^[-*+]\s/m,
  // Ordered lists: "1. item"
  /^\d+\.\s/m,
  // Blockquotes: "> quote"
  /^>\s/m,
  // Bold / italic markers
  /\*\*(?=\S)([\s\S]*?\S)\*\*/,
  /__(?=\S)([\s\S]*?\S)__/,
  /(?<!\*)\*(?=\S)([\s\S]*?\S)\*(?!\*)/,
  /(?<!_)_(?=\S)([\s\S]*?\S)_(?!_)/,
]

// Patterns that indicate pasted content is source code that should be wrapped in code fences
const CODE_PATTERNS: { pattern: RegExp; language: string }[] = [
  // HTML/XML - doctype, html tags, or XML declaration
  { pattern: /^<!DOCTYPE\s+html/i, language: 'html' },
  { pattern: /^<\?xml\s+version/i, language: 'xml' },
  { pattern: /^<html[\s>]/i, language: 'html' },
  // JSON - starts with { or [ and looks like JSON
  { pattern: /^[\s]*[{\[][\s\S]*[}\]][\s]*$/, language: 'json' },
  // JavaScript/TypeScript patterns
  { pattern: /^(import|export|const|let|var|function|class|interface|type)\s+/m, language: 'typescript' },
  // Python
  { pattern: /^(def|class|import|from|if __name__)\s+/m, language: 'python' },
  // CSS
  { pattern: /^(@import|@media|@keyframes|[.#]?[\w-]+\s*\{)/m, language: 'css' },
  // SQL
  { pattern: /^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\s+/im, language: 'sql' },
  // Shell/Bash
  { pattern: /^#!\/bin\/(bash|sh|zsh)/m, language: 'bash' },
]

function detectCodeLanguage(text: string): string | null {
  const trimmed = text.trim()
  if (!trimmed) return null

  // Don't wrap if already contains code fence markers
  if (trimmed.includes('```')) {
    return null
  }

  for (const { pattern, language } of CODE_PATTERNS) {
    if (pattern.test(trimmed)) {
      return language
    }
  }
  return null
}

function hasMarkdownSyntax(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false

  return MARKDOWN_PATTERNS.some((pattern) => pattern.test(trimmed))
}

// Lazily create a shared Turndown instance so we don't reconfigure on every paste
let turndownService: TurndownService | null = null

function getTurndown(): TurndownService {
  if (!turndownService) {
    turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      bulletListMarker: '-',
    })
  }
  return turndownService
}

/**
 * Smart paste handler for markdown content.
 *
 * - If plain text already looks like markdown, returns it as-is.
 * - If plain text looks like source code, wraps it in a fenced code block.
 * - If HTML is available but plain text looks "rendered", converts HTML → markdown.
 * - Otherwise returns null to allow default paste behavior.
 */
export function handleMarkdownPaste(e: ClipboardEventLike): string | null {
  const data = e.clipboardData
  if (!data) return null

  const plainText = data.getData('text/plain') ?? ''
  const html = data.getData('text/html') ?? ''

  // If the user is pasting raw markdown, preserve it exactly
  if (plainText && hasMarkdownSyntax(plainText)) {
    return plainText
  }

  // Check if the pasted content looks like source code
  const detectedLanguage = detectCodeLanguage(plainText)
  if (detectedLanguage) {
    return `\`\`\`${detectedLanguage}\n${plainText.trim()}\n\`\`\``
  }

  // If we have HTML and the plain text doesn't look like markdown,
  // treat this as rendered rich text and convert back to markdown.
  if (html && !hasMarkdownSyntax(plainText)) {
    const service = getTurndown()
    const markdown = service.turndown(html)
    if (markdown.trim()) {
      return markdown
    }
  }

  // Fall back to the browser's default paste behavior
  return null
}


